/**
 * Free-text time fields (placeholder „HH:MM", D-441): what a user types is read as a time of day and written back as
 * `HH:MM` on blur and whenever the value is used — `1000` → `10:00`, `930` → `09:30`, `9` → `09:00`,
 * `9.30` / `9,30` / `9 30` → `09:30`, `10:0` → `10:00`. Anything unreadable stays as typed, so the field's own
 * validation message still shows.
 */

/** `HH:MM` for a readable time, null for an empty or unreadable text. */
export function parseTimeInput(text: string): string | null {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  let hours: number;
  let minutes: number;
  const separated = /^(\d{1,2})\s*[:.,h ]\s*(\d{1,2})$/i.exec(trimmed);
  if (separated) {
    hours = Number(separated[1]);
    // „10:0" is ten o'clock, „9:3" half past nine is not guessed: a single minute digit means tens only when it is 0.
    const minuteText = separated[2] ?? '';
    if (minuteText.length === 1 && minuteText !== '0') return null;
    minutes = Number(minuteText);
  } else if (/^\d{1,4}$/.test(trimmed)) {
    if (trimmed.length <= 2) {
      hours = Number(trimmed);
      minutes = 0;
    } else {
      hours = Number(trimmed.slice(0, trimmed.length - 2));
      minutes = Number(trimmed.slice(-2));
    }
  } else {
    return null;
  }
  if (hours > 23 || minutes > 59) return null;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/** The text a time field shows after blur: the normalised `HH:MM`, or the trimmed text when it is not a time. */
export function normalizeTimeInput(text: string): string {
  return parseTimeInput(text) ?? text.trim();
}
